import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { ProductSummary } from '@/api'
import { cn } from '@/lib/cn'
import { ChevronLeftIcon, ChevronRightIcon } from '@/components/ui/icons'
import { ProductCard, ProductCardSkeleton } from './ProductCard'

interface ProductCarouselProps {
  products: ProductSummary[]
  loading?: boolean
  /** Título da prateleira. Quando ausente, o cabeçalho não é renderizado. */
  title?: ReactNode
  /** Link "Ver lista completa" à direita do título. */
  action?: ReactNode
  /**
   * Cards visíveis por página no desktop. Telas menores usam sempre 2 (celular)
   * e 3 (tablet), porque abaixo disso o card fica ilegível.
   */
  columns?: 4 | 5
  className?: string
}

/**
 * Largura de cada card, já descontando a parte do gap (8px) que lhe cabe, para
 * que um número exato de cards ocupe a página — sem meio card sobrando na borda.
 */
const COLUMN_WIDTH: Record<4 | 5, string> = {
  4: 'lg:w-[calc(25%-0.375rem)]',
  5: 'lg:w-[calc(20%-0.4rem)]',
}

/**
 * Prateleira horizontal com scroll-snap.
 *
 * Rola de página em página (largura visível), mantém setas e indicadores em
 * sincronia com o scroll — inclusive quando o usuário arrasta com o dedo — e
 * esconde as setas quando não há para onde ir.
 */
export function ProductCarousel({
  products,
  loading,
  title,
  action,
  columns = 5,
  className,
}: ProductCarouselProps) {
  const railRef = useRef<HTMLDivElement>(null)
  const [pages, setPages] = useState(1)
  const [page, setPage] = useState(0)

  /**
   * Largura de uma página, medida pelos próprios cards.
   *
   * Deriva do primeiro filho em vez de `clientWidth` porque o trilho tem
   * padding (o respiro da sombra), e `clientWidth`/`scrollWidth` o incluem —
   * paginar por eles acumularia erro a cada página.
   */
  const metrics = useCallback(() => {
    const rail = railRef.current
    const first = rail?.firstElementChild as HTMLElement | null
    if (!rail || !first || first.offsetWidth === 0) return null

    const styles = getComputedStyle(rail)
    const inner =
      rail.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight)
    const step = first.offsetWidth + parseFloat(styles.columnGap || '0')
    const perPage = Math.max(1, Math.round(inner / step))

    return { pageWidth: perPage * step, count: rail.childElementCount, perPage }
  }, [])

  const sync = useCallback(() => {
    const m = metrics()
    if (!m) return
    setPages(Math.max(1, Math.ceil(m.count / m.perPage)))
    setPage(Math.round((railRef.current?.scrollLeft ?? 0) / m.pageWidth))
  }, [metrics])

  useEffect(() => {
    sync()
    const rail = railRef.current
    if (!rail) return

    const observer = new ResizeObserver(sync)
    observer.observe(rail)
    return () => observer.disconnect()
  }, [sync, products.length])

  const goToPage = (index: number) => {
    const m = metrics()
    if (!m) return
    railRef.current?.scrollTo({ left: index * m.pageWidth, behavior: 'smooth' })
  }

  const atStart = page <= 0
  const atEnd = page >= pages - 1

  return (
    <section className={cn('relative', className)}>
      {title && (
        <div className="mb-4 flex shrink-0 items-center gap-4">
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <h2 className="text-[15px] font-semibold text-ink lg:text-[17px]">{title}</h2>
            {action}
          </div>
          <CarouselDots
            className="ml-auto shrink-0"
            count={pages}
            activeIndex={page}
            onSelect={goToPage}
          />
        </div>
      )}

      <div className="relative">
        <div
          ref={railRef}
          onScroll={sync}
          className="scroll-rail -mx-1 flex gap-2 overflow-x-auto px-1"
        >
          {(loading ? Array.from({ length: columns }) : products).map((item, index) => (
            <div
              key={loading ? index : (item as ProductSummary).id}
              className={cn(
                'w-[calc(50%-0.25rem)] shrink-0 snap-start sm:w-[calc(33.333%-0.334rem)]',
                COLUMN_WIDTH[columns],
              )}
            >
              {loading ? (
                <ProductCardSkeleton />
              ) : (
                <ProductCard product={item as ProductSummary} variant="shelf" />
              )}
            </div>
          ))}
        </div>

        {!atStart && <RailArrow direction="left" onClick={() => goToPage(page - 1)} />}
        {!atEnd && pages > 1 && (
          <RailArrow direction="right" onClick={() => goToPage(page + 1)} />
        )}
      </div>
    </section>
  )
}

function RailArrow({
  direction,
  onClick,
}: {
  direction: 'left' | 'right'
  onClick: () => void
}) {
  const Icon = direction === 'left' ? ChevronLeftIcon : ChevronRightIcon
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={direction === 'left' ? 'Ver anteriores' : 'Ver próximos'}
      className={cn(
        'absolute top-1/2 z-20 hidden size-11 -translate-y-1/2 items-center justify-center rounded-full bg-surface text-accent shadow-pop transition-colors hover:text-accent-dark md:flex',
        direction === 'left' ? '-left-5' : '-right-5',
      )}
    >
      <Icon className="size-6" strokeWidth={2.4} />
    </button>
  )
}

interface CarouselDotsProps {
  count: number
  activeIndex: number
  onSelect: (index: number) => void
  className?: string
}

/** Indicadores de página do cabeçalho da prateleira. */
function CarouselDots({ count, activeIndex, onSelect, className }: CarouselDotsProps) {
  if (count <= 1) return null

  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      {Array.from({ length: count }).map((_, index) => (
        <button
          key={index}
          type="button"
          onClick={() => onSelect(index)}
          aria-label={`Ir para a página ${index + 1}`}
          aria-current={index === activeIndex}
          className={cn(
            'size-1.5 rounded-full transition-colors',
            index === activeIndex ? 'bg-accent' : 'bg-line-strong hover:bg-ink-faint',
          )}
        />
      ))}
    </div>
  )
}
